import React from "react";

/**
 * Shared layout for legal / policy pages (Terms, Privacy) so they stay visually
 * consistent with the rest of the site: hero header, quick-nav chips, a clean
 * content panel, and an optional info-card row.
 */

export const LegalSection = ({ id, number, title, children }) => (
  <section id={id} className="scroll-mt-28">
    <h3 className="flex items-center gap-3 font-heading font-bold text-lg sm:text-xl text-white mt-8 mb-3 first:mt-0">
      {number != null && (
        <span className="flex items-center justify-center h-8 w-8 shrink-0 rounded-lg bg-brand-purple/15 border border-brand-purple/30 text-brand-gold text-sm font-extrabold">
          {number}
        </span>
      )}
      {title}
    </h3>
    <div className="text-white/70 leading-relaxed space-y-2 sm:pl-11">{children}</div>
  </section>
);

export default function LegalShell({
  icon,
  title,
  updated,
  intro,
  toc = [],
  infoCards = [],
  children,
}) {
  return (
    <div className="min-h-[calc(100vh-var(--header-height))] py-8 sm:py-10 px-3 sm:px-6 md:px-8">
      <div className="max-w-[1000px] mx-auto">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-surface-card/40 backdrop-blur-md mb-6 px-6 sm:px-10 py-10 sm:py-12 text-center">
          <div className="absolute inset-0 gg-grid-backdrop opacity-40" />
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-52 w-[70%] rounded-full bg-brand-purple/25 blur-3xl animate-pulseGlow" />
          <div className="relative animate-fadeUp">
            <span className="flex items-center justify-center h-16 w-16 mx-auto rounded-2xl bg-brand-gold/15 border border-brand-gold/30 text-3xl mb-4">
              {icon}
            </span>
            <h1 className="font-heading font-extrabold text-white text-3xl sm:text-4xl tracking-tight">
              {title}
            </h1>
            {updated && <p className="text-white/50 text-sm mt-3">Last updated: {updated}</p>}
          </div>
        </div>

        <div className="rounded-3xl border border-white/10 bg-surface-card/40 backdrop-blur-md p-6 sm:p-9 animate-fadeUp">
          {intro && <p className="text-white/75 leading-relaxed mb-6">{intro}</p>}

          {toc.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8 pb-6 border-b border-white/10">
              {toc.map((t) => (
                <a
                  key={t.id}
                  href={`#${t.id}`}
                  className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-semibold text-white/70 hover:text-brand-gold hover:border-brand-gold/40 transition-colors"
                >
                  {t.label}
                </a>
              ))}
            </div>
          )}

          {children}
        </div>

        {infoCards.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            {infoCards.map((c) => (
              <div
                key={c.title}
                className="rounded-2xl border border-white/10 bg-surface-card/40 backdrop-blur-md p-5 text-center"
              >
                <span
                  className="flex items-center justify-center h-12 w-12 mx-auto rounded-2xl text-2xl mb-3 border"
                  style={{ borderColor: `${c.color}55`, background: `${c.color}1f` }}
                >
                  {c.icon}
                </span>
                <h3 className="font-heading font-bold text-white mb-1">{c.title}</h3>
                <p className="text-white/55 text-sm">{c.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
